/* eslint-disable camelcase */
// the unused vars need to be there otherwise the component doesn't rerender on those property change
/* eslint-disable no-unused-vars */
import React from 'react';
import { observer } from 'mobx-react';
import s from './BusinessInfoTab.module.css';
import {
  Card,
  Text,
  HSpacer,
  Tooltip,
  Icon,
  ContentSnippet,
  VSpacer,
  Button,
  LoadingOverlay,
} from '@mqd/volt-base';
import PropTypes from 'prop-types';
import { AddIdentificationModal } from '../shared-components';
import { ParentDetailCard } from '@mq/voltron-parent';
import { StoreContext } from '@mqd/volt-contexts';
import BusinessDetailsHome from 'components/business-details/BusinessDetailsHome.js';
import BusinessDetailsBaseInfo from 'components/business-details/BusinessDetailsBaseInfo.js';

class BusinessProprietorOrOfficerCard extends ParentDetailCard {
  constructor(props) {
    super(props);
    this.handleSave = this.handleSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.state = {
      editActive: false,
      showAddIdentificationModal: false,
      loading: false,
    };
  }

  get businessStore() {
    const { businessStore } = this.props;
    return businessStore || {};
  }

  get poe() {
    const { businessStore = {} } = this.props;
    const { proprietor_or_officer = {} } = businessStore;
    return proprietor_or_officer || {};
  }

  async handleSave() {
    const { updateBusiness } = this.businessStore;
    this.setState({ loading: true });
    const success = await updateBusiness();
    if (success) {
      this.setState({ loading: false, editActive: false });
    } else {
      this.setState({ loading: false });
    }
  }

  handleCancel() {
    const { revertUpdates } = this.businessStore;
    revertUpdates && revertUpdates();
    this.setState({ editActive: false });
  }

  renderHeaderButtons() {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button type="outline" onClick={this.handleCancel}>
          Cancel
        </Button>
        <HSpacer />
        <Button onClick={this.handleSave}>Save</Button>
      </div>
    );
  }

  renderTitle() {
    return (
      <div className={s.flexDiv}>
        <Text type="h5">Proprietor Or Officer</Text>
        <HSpacer />
        <div style={{ height: 24 }}>
          <Tooltip
            zIndex={9999}
            content={`
                If a businesses incoporation type is
                SOLE_PROPRIETORSHIP information about
                the proprietor or officer will be used
                for verification (KYC) checks.
              `}
          >
            <Icon type="info" mod="disabled" hasTooltip />
          </Tooltip>
        </div>
      </div>
    );
  }

  renderHome() {
    const { editActive } = this.state;
    const { home = {} } = this.poe;

    return (
      <BusinessDetailsHome
        ownerType="business proprietor or officer"
        editActive={editActive}
        home={home}
        renderSnippet={this.renderSnippet}
      />
    );
  }

  renderIdentifications() {
    const { canRevealIdentification, uamGranularPermissionsActive } = this.props;
    const { editActive } = this.state;
    const { revealIdentification, revealIdentificationGranular, hideIdentification } =
      this.businessStore;
    let { identifications, isEdit } = this.poe;
    if (!identifications) {
      identifications = [];
    }
    const snippets = identifications.map((identification, i) => {
      const { type, value, expiration_date, loading, valueWithStars } = identification;
      return (
        <LoadingOverlay active={loading} key={i}>
          <ContentSnippet title={type} key={i} horizontalLayout>
            <div className={s.spaceAround}>
              <Text>{valueWithStars}</Text>
              {this.renderSsnRevealButton({
                editActive,
                canReveal: canRevealIdentification,
                value: valueWithStars,
                reveal: () =>
                  uamGranularPermissionsActive
                    ? revealIdentificationGranular(type)
                    : revealIdentification(type),
                hide: () => hideIdentification(type),
              })}
            </div>
            {expiration_date ? (
              <div className={s.idExpires}>
                <Text mod="light">Expires: {expiration_date}</Text>
              </div>
            ) : null}
          </ContentSnippet>
          <VSpacer factor={2} />
        </LoadingOverlay>
      );
    });
    return (
      <div data-testid="v2-identifications">
        <VSpacer factor={2} />
        <div className={s.flexDiv}>
          <Text type="h5">Identifications</Text>
          <HSpacer factor={0.5} />
          <div style={{ height: 22 }}>
            <Tooltip
              zIndex={9999}
              content={
                <div>
                  Forms of identification used to validate the proprietor or officer.
                  <br />
                  <span style={{ fontWeight: 'bold' }}>
                    Required for verification (KYC) checks.
                  </span>
                  <br />
                  <br />
                  <span style={{ fontWeight: 'bold' }}>Note:</span> Only one of the following
                  identification types can be associated with a business:
                  <ul>
                    <li>SSN – Social Security Number</li>
                    <li>SIN – Social Insurance Number</li>
                    <li>TIN – Taxpayer Identification Number</li>
                    <li>NIN – National Insurance Number</li>
                  </ul>
                  <br />
                  <span style={{ fontWeight: 'bold' }}>Also Note:</span> SSN is required for KYC
                  verification checks.
                </div>
              }
            >
              <Icon type="info" mod="disabled" hasTooltip />
            </Tooltip>
          </div>
          <HSpacer factor={2} />
          <Button
            size="xs"
            type="outline"
            onClick={() => this.setState({ showAddIdentificationModal: true })}
            testId={`${isEdit ? 'Edit' : 'Add New'}_button`}
          >
            {isEdit ? 'Edit' : 'Add New'}
          </Button>
        </div>
        <VSpacer factor={2} />
        {snippets}
      </div>
    );
  }

  renderProprietorIsBeneficialOwner() {
    const { editActive } = this.state;
    const { proprietor_is_beneficial_owner, updateForSave } = this.businessStore;

    return (
      <>
        <VSpacer factor={2} />
        {this.renderSnippet({
          title: 'Proprietor or Officer is Beneficial Owner',
          content: proprietor_is_beneficial_owner ? 'True' : 'False',
          inputValue: proprietor_is_beneficial_owner,
          inputType: 'toggle',
          editActive: editActive,
          onChange: (value) => {
            updateForSave('proprietor_is_beneficial_owner', value);
          },
          tooltipContent: `Indicates if proprietor or officer is a beneficial owner.`,
        })}
      </>
    );
  }

  renderAddIdentificationModal() {
    const { showAddIdentificationModal, loading } = this.state;
    const { addPoeIdentification } = this.businessStore;
    const { getIdentification, isEdit } = this.poe;
    if (!showAddIdentificationModal) return null;
    return (
      <LoadingOverlay active={loading}>
        <AddIdentificationModal
          isEdit={isEdit}
          values={getIdentification}
          options={['SSN', 'SIN', 'TIN', 'NIN']}
          disableExpirationDateFor={['SSN', 'SIN', 'TIN', 'NIN']}
          handleSave={async ({ type, value, expirationDate }) => {
            this.setState({ loading: true, showAddIdentificationModal: false });
            addPoeIdentification && (await addPoeIdentification({ type, value, expirationDate }));
            this.setState({ loading: false });
          }}
          hideModal={() => {
            this.setState({ showAddIdentificationModal: false });
          }}
        />
      </LoadingOverlay>
    );
  }

  renderV1Identification() {
    const { editActive } = this.state;
    const { ssn, updateForSave, loading, ssnWithStars } = this.poe;
    const { canRevealIdentification, uamGranularPermissionsActive } = this.props;
    const { revealV1Identification, revealV1IdentificationGranular, hideV1Identification } =
      this.businessStore;
    return (
      <div data-testid="v1-identifications">
        <VSpacer factor={2} />
        <div className={s.spaceBetween}>
          <div className={s.flexDiv}>
            <Text type="h5">Identifications</Text>
            <HSpacer factor={0.5} />
            <div style={{ height: 22 }}>
              <Tooltip
                zIndex={9999}
                content={`
                    Forms of identification used to validate the proprietor or officer.
                    Required for verification (KYC) checks. SSN needs to be 9 digits
                    Note: SSN is required for KYC
                    verification checks.
                  `}
              >
                <Icon type="info" mod="disabled" hasTooltip />
              </Tooltip>
            </div>
          </div>
          {this.renderSsnRevealButton({
            editActive,
            canReveal: canRevealIdentification,
            value: ssnWithStars,
            reveal: () =>
              uamGranularPermissionsActive
                ? revealV1IdentificationGranular()
                : revealV1Identification(),
            hide: () => hideV1Identification(),
          })}
        </div>
        <VSpacer factor={2} />
        <LoadingOverlay active={loading}>
          {this.renderSnippet({
            title: 'SSN',
            content: ssnWithStars,
            editActive: editActive,
            onChange: (e) => updateForSave('ssn', e.target.value),
            tooltipContent: 'SSN of the proprieter SSN',
          })}
        </LoadingOverlay>
      </div>
    );
  }

  renderBaseInfo() {
    const { editActive } = this.state;

    return (
      <BusinessDetailsBaseInfo
        ownerType="business proprietor or officer"
        editActive={editActive}
        person={this.poe}
        renderSnippet={this.renderSnippet}
      />
    );
  }

  render() {
    const { loading } = this.state;
    const { canRevealIdentification } = this.props;
    const { editable } = this.props;
    const { editActive } = this.state;
    const { ssn, loading: poeLoading, identifications } = this.poe;
    // if observables are not declared at where the context is called, a re-render doesn't happen
    const {
      loading: identificationLoading,
      value,
      type,
    } = (identifications && identifications[0]) || {};
    return (
      <LoadingOverlay active={loading}>
        {this.renderAddIdentificationModal()}
        <Card
          title={this.renderTitle()}
          testId="business-propritor-or-office"
          controls={editable && (editActive ? this.renderHeaderButtons() : this.renderControl())}
        >
          {this.renderBaseInfo()}
          {this.renderHome()}
          {this.renderProprietorIsBeneficialOwner()}
          <StoreContext.Consumer>
            {({ ProgramConfigStore = {} }) => {
              return ProgramConfigStore.isV2
                ? this.renderIdentifications()
                : this.renderV1Identification();
            }}
          </StoreContext.Consumer>
        </Card>
      </LoadingOverlay>
    );
  }
}

BusinessProprietorOrOfficerCard.propTypes = {
  businessStore: PropTypes.any,
  canRevealIdentification: PropTypes.bool,
  editable: PropTypes.bool,
};

BusinessProprietorOrOfficerCard.defaultProps = {
  businessStore: {},
  canRevealIdentification: false,
  editable: false,
};

export default observer(BusinessProprietorOrOfficerCard);
